import React from 'react';

/* ============================================================================
   Self-generated SVG illustrations + brand marks for the legal / cookie pages.
   Colours follow the MainEvents logo gradient (blue #5B8FE8 -> purple #6C3DD3).
   Each gradient gets a unique id so several illustrations can coexist on a page.
   ========================================================================== */

const BLUE = '#5B8FE8';
const PURPLE = '#6C3DD3';

type IlloProps = { className?: string };

/* Brand wordmark + pin icon shipped in /public (referenced from the build root). */
export function BrandLogo({ className }: IlloProps) {
  return (
    <img
      src={`${process.env.PUBLIC_URL}/mainevents_logo.svg`}
      alt="MainEvents"
      className={className}
    />
  );
}

export function BrandIcon({ className }: IlloProps) {
  return (
    <img
      src={`${process.env.PUBLIC_URL}/mainevents_icon.svg`}
      alt="MainEvents"
      className={className}
    />
  );
}

/* ----------------------------------------------------------------- cookie */
export function CookieIllustration({ className }: IlloProps) {
  return (
    <svg viewBox="0 0 200 200" className={className} role="img" aria-label="Cookies">
      <defs>
        <linearGradient id="ck-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={BLUE} />
          <stop offset="100%" stopColor={PURPLE} />
        </linearGradient>
        <mask id="ck-bite">
          <rect width="200" height="200" fill="#fff" />
          <circle cx="168" cy="52" r="34" fill="#000" />
        </mask>
      </defs>
      <circle cx="96" cy="104" r="80" fill="url(#ck-grad)" mask="url(#ck-bite)" />
      {/* chips */}
      <circle cx="72" cy="70" r="11" fill="#fff" opacity="0.85" />
      <circle cx="118" cy="92" r="9" fill="#fff" opacity="0.7" />
      <circle cx="64" cy="120" r="8" fill="#fff" opacity="0.75" />
      <circle cx="104" cy="138" r="12" fill="#fff" opacity="0.85" />
      <circle cx="138" cy="132" r="7" fill="#fff" opacity="0.6" />
      <circle cx="92" cy="100" r="6" fill="#fff" opacity="0.55" />
    </svg>
  );
}

/* cookie mark for the gradient consent header (light on colour) */
export function CookieMark({ className }: IlloProps) {
  return (
    <svg viewBox="0 0 64 64" className={className} role="img" aria-label="Cookies">
      <circle cx="32" cy="32" r="26" fill="#fff" opacity="0.18" />
      <circle cx="32" cy="32" r="20" fill="#fff" />
      <circle cx="25" cy="26" r="3.4" fill={PURPLE} />
      <circle cx="39" cy="30" r="2.8" fill={BLUE} />
      <circle cx="29" cy="39" r="3" fill={BLUE} />
      <circle cx="40" cy="40" r="2.4" fill={PURPLE} />
    </svg>
  );
}

/* --------------------------------------------------------------- privacy */
export function PrivacyShieldIllustration({ className }: IlloProps) {
  return (
    <svg viewBox="0 0 200 200" className={className} role="img" aria-label="Datenschutz">
      <defs>
        <linearGradient id="sh-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={BLUE} />
          <stop offset="100%" stopColor={PURPLE} />
        </linearGradient>
      </defs>
      <path
        d="M100 18 L168 44 V104 C168 146 138 172 100 184 C62 172 32 146 32 104 V44 Z"
        fill="url(#sh-grad)"
      />
      <path
        d="M100 30 L156 52 V104 C156 138 132 160 100 171 V30 Z"
        fill="#fff"
        opacity="0.12"
      />
      {/* lock */}
      <rect x="74" y="96" width="52" height="44" rx="8" fill="#fff" />
      <path
        d="M84 96 V84 a16 16 0 0 1 32 0 V96"
        fill="none"
        stroke="#fff"
        strokeWidth="9"
      />
      <circle cx="100" cy="114" r="7" fill={PURPLE} />
      <rect x="96.5" y="116" width="7" height="14" rx="3.5" fill={PURPLE} />
    </svg>
  );
}

/* ------------------------------------------------------------- impressum */
export function ImpressumBuildingIllustration({ className }: IlloProps) {
  return (
    <svg viewBox="0 0 200 200" className={className} role="img" aria-label="Impressum">
      <defs>
        <linearGradient id="bd-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={BLUE} />
          <stop offset="100%" stopColor={PURPLE} />
        </linearGradient>
      </defs>
      <rect x="44" y="40" width="86" height="132" rx="10" fill="url(#bd-grad)" />
      <rect x="120" y="84" width="44" height="88" rx="8" fill={PURPLE} opacity="0.85" />
      {/* windows */}
      {[58, 84, 110].map((x) =>
        [60, 88, 116, 144].map((y) => (
          <rect key={`${x}-${y}`} x={x} y={y} width="14" height="14" rx="3" fill="#fff" opacity="0.9" />
        ))
      )}
      {[132, 150].map((x) =>
        [100, 124, 148].map((y) => (
          <rect key={`w${x}-${y}`} x={x} y={y} width="11" height="11" rx="2.5" fill="#fff" opacity="0.8" />
        ))
      )}
      <rect x="78" y="148" width="20" height="24" rx="3" fill="#fff" opacity="0.95" />
    </svg>
  );
}

/* ------------------------------------------------------ third-party plug */
export function ThirdPartyIllustration({ className }: IlloProps) {
  return (
    <svg viewBox="0 0 200 200" className={className} role="img" aria-label="Drittanbieter">
      <defs>
        <linearGradient id="tp-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={BLUE} />
          <stop offset="100%" stopColor={PURPLE} />
        </linearGradient>
      </defs>
      <circle cx="64" cy="100" r="40" fill="url(#tp-grad)" />
      <circle cx="140" cy="64" r="26" fill={PURPLE} opacity="0.85" />
      <circle cx="142" cy="138" r="30" fill={BLUE} opacity="0.85" />
      <path d="M92 90 L118 70" stroke={PURPLE} strokeWidth="7" strokeLinecap="round" />
      <path d="M96 112 L116 132" stroke={BLUE} strokeWidth="7" strokeLinecap="round" />
      <circle cx="64" cy="100" r="15" fill="#fff" opacity="0.9" />
    </svg>
  );
}

/* --------------------------------------------------- help-center hero scene */
/* Wide decorative scene for the Help-Center hero. Self-contained: it paints its
   own brand gradient so text can be overlaid on top (see .se-hc-hero). Motifs
   (headset, "?" speech bubble, ticket, sparkles) cluster on the right so the
   left stays calm for the heading. */
export function HelpCenterHeroArt({ className }: IlloProps) {
  const sparkle = (cx: number, cy: number, r: number, op = 0.9) => (
    <path
      key={`sp-${cx}-${cy}`}
      d={`M${cx} ${cy - r} Q${cx} ${cy} ${cx + r} ${cy} Q${cx} ${cy} ${cx} ${cy + r} Q${cx} ${cy} ${cx - r} ${cy} Q${cx} ${cy} ${cx} ${cy - r} Z`}
      fill="#fff"
      opacity={op}
    />
  );
  return (
    <svg
      viewBox="0 0 1200 520"
      className={className}
      preserveAspectRatio="xMidYMid slice"
      role="img"
      aria-label="Hilfe-Center"
    >
      <defs>
        <linearGradient id="hc-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={BLUE} />
          <stop offset="100%" stopColor={PURPLE} />
        </linearGradient>
      </defs>

      {/* gradient backdrop + depth circles */}
      <rect width="1200" height="520" fill="url(#hc-grad)" />
      <circle cx="1050" cy="90" r="230" fill="#fff" opacity="0.06" />
      <circle cx="170" cy="500" r="280" fill="#fff" opacity="0.05" />
      <circle cx="990" cy="470" r="150" fill="#fff" opacity="0.05" />

      {/* headset */}
      <path d="M940 92 A48 48 0 0 1 1036 92" stroke="#fff" strokeWidth="11" fill="none" strokeLinecap="round" />
      <rect x="930" y="88" width="20" height="42" rx="9" fill="#fff" />
      <rect x="1026" y="88" width="20" height="42" rx="9" fill="#fff" />
      <path d="M1036 130 C1036 158 1016 166 996 166" stroke="#fff" strokeWidth="9" fill="none" strokeLinecap="round" />
      <circle cx="989" cy="166" r="8" fill="#fff" />

      {/* big "?" speech bubble */}
      <rect x="812" y="150" width="236" height="158" rx="30" fill="#fff" />
      <path d="M856 304 L842 344 L902 306 Z" fill="#fff" />
      <text x="930" y="266" textAnchor="middle" fontFamily="Arial, sans-serif" fontWeight="900" fontSize="120" fill={PURPLE}>?</text>

      {/* small check bubble */}
      <rect x="1030" y="300" width="92" height="70" rx="22" fill="#fff" opacity="0.96" />
      <path d="M1050 336 l15 16 l28 -33" stroke={BLUE} strokeWidth="9" fill="none" strokeLinecap="round" strokeLinejoin="round" />

      {/* ticket */}
      <g transform="rotate(-8 960 405)">
        <rect x="838" y="364" width="244" height="82" rx="16" fill="#fff" opacity="0.95" />
        <line x1="1014" y1="372" x2="1014" y2="438" stroke={BLUE} strokeWidth="3" strokeDasharray="4 7" opacity="0.6" />
        <circle cx="884" cy="405" r="9" fill={PURPLE} opacity="0.35" />
        <circle cx="918" cy="405" r="9" fill={PURPLE} opacity="0.35" />
        <circle cx="952" cy="405" r="9" fill={PURPLE} opacity="0.35" />
        <circle cx="1050" cy="405" r="16" fill="url(#hc-grad)" opacity="0.2" />
      </g>

      {/* sparkles + dots */}
      {sparkle(760, 150, 16, 0.9)}
      {sparkle(1136, 214, 12, 0.85)}
      {sparkle(905, 472, 14, 0.8)}
      {sparkle(700, 408, 10, 0.7)}
      <circle cx="1152" cy="360" r="5" fill="#fff" opacity="0.5" />
      <circle cx="792" cy="300" r="6" fill="#fff" opacity="0.5" />
      <circle cx="1104" cy="150" r="5" fill="#fff" opacity="0.5" />
    </svg>
  );
}

/* ----------------------------------------------------- terms / rulebook */
export function TermsIllustration({ className }: IlloProps) {
  return (
    <svg viewBox="0 0 200 200" className={className} role="img" aria-label="Nutzungsbedingungen">
      <defs>
        <linearGradient id="tm-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={BLUE} />
          <stop offset="100%" stopColor={PURPLE} />
        </linearGradient>
      </defs>
      {/* document */}
      <rect x="44" y="26" width="112" height="148" rx="12" fill="url(#tm-grad)" />
      <rect x="44" y="26" width="112" height="148" rx="12" fill="#fff" opacity="0.08" />
      {/* text lines */}
      {[58, 78, 98, 118].map((y) => (
        <rect key={y} x="62" y={y} width="76" height="8" rx="4" fill="#fff" opacity="0.85" />
      ))}
      <rect x="62" y="138" width="46" height="8" rx="4" fill="#fff" opacity="0.6" />
      {/* check seal */}
      <circle cx="138" cy="146" r="26" fill="#fff" />
      <circle cx="138" cy="146" r="26" fill="url(#tm-grad)" opacity="0.18" />
      <path d="M126 146 l8 9 l16 -19" stroke={PURPLE} strokeWidth="6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ------------------------------------------------------------- fees/coins */
export function FeesIllustration({ className }: IlloProps) {
  return (
    <svg viewBox="0 0 200 200" className={className} role="img" aria-label="Gebühren">
      <defs>
        <linearGradient id="fe-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={BLUE} />
          <stop offset="100%" stopColor={PURPLE} />
        </linearGradient>
      </defs>
      <ellipse cx="100" cy="150" rx="58" ry="20" fill={PURPLE} opacity="0.85" />
      <ellipse cx="100" cy="120" rx="58" ry="20" fill="url(#fe-grad)" />
      <ellipse cx="100" cy="120" rx="58" ry="20" fill="none" stroke="#fff" strokeWidth="2" opacity="0.4" />
      <circle cx="120" cy="66" r="40" fill="url(#fe-grad)" />
      <circle cx="120" cy="66" r="30" fill="#fff" opacity="0.18" />
      <text
        x="120"
        y="78"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontWeight="900"
        fontSize="38"
        fill="#fff"
      >
        €
      </text>
    </svg>
  );
}
